import { useEffect, useRef, useState } from "react";
import { CinematicMomentCard } from "@/components/cinematic/CinematicMomentCard";
import { SHOWCASE_SCRIPT } from "@/lib/showcase/showcaseScript";
import { showcase, useShowcaseMode } from "@/lib/showcase/showcaseMode";
import { acquireSlot, releaseSlot } from "@/lib/launchHardening/priorityVisualQueue";
import type { CinematicMoment } from "@/lib/cinematic/cinematicMomentsEngine";

/**
 * Renders the current scripted beat as a CinematicMomentCard, gated by
 * the PriorityVisualQueue so it cooperates with other strong visuals.
 * Advances on a dwell timer when `playing` is true.
 */
export function ShowcaseStage() {
  const s = useShowcaseMode();
  const beat = SHOWCASE_SCRIPT[s.beatIndex % SHOWCASE_SCRIPT.length];
  const [tick, setTick] = useState(0);
  const slotRef = useRef<string | null>(null);

  // Advance beat on dwell while playing.
  useEffect(() => {
    if (!s.active || !s.playing) return;
    const id = window.setTimeout(() => {
      showcase.next(SHOWCASE_SCRIPT.length);
      setTick((t) => t + 1);
    }, beat.dwellMs);
    return () => window.clearTimeout(id);
  }, [s.active, s.playing, s.beatIndex, beat.dwellMs]);

  // Acquire a priority slot per beat so the queue knows the showcase is "on stage".
  useEffect(() => {
    if (!s.active) {
      releaseSlot(slotRef.current);
      slotRef.current = null;
      return;
    }
    releaseSlot(slotRef.current);
    slotRef.current = acquireSlot("cinematic_moment", "high", `showcase-${s.beatIndex}-${tick}`);
    return () => {
      releaseSlot(slotRef.current);
      slotRef.current = null;
    };
  }, [s.active, s.beatIndex, tick]);

  if (!s.active) return null;

  const moment: CinematicMoment = {
    ...beat,
    id: `showcase-${s.beatIndex}-${tick}`,
    createdAt: Date.now(),
  };

  return (
    <div className="space-y-2">
      <CinematicMomentCard key={moment.id} moment={moment} />
    </div>
  );
}
